import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Grid, 
  FormControlLabel,
  Switch, 
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Tabs,
  Tab,
  InputAdornment,
  IconButton,
  FormHelperText
} from '@mui/material';
import { Save, ArrowLeft, X } from 'lucide-react';
import InputMask from 'react-input-mask';
import { PersonFormData } from '../../types/person';
import { Profile } from '../../types/profile';
import { ProfileService } from '../../services/profileService';
import { PersonService } from '../../services/personService';
import ConfirmationDialog from '../../components/common/ConfirmationDialog';
import SuccessSnackbar from '../../components/common/SuccessSnackbar';

// Mock data (substituir pela chamada de API)
const mockProfiles: Profile[] = [
  { id: 1, name: 'Administrador', ativo: true, permissions: {}, createdAt: '', updatedAt: '' },
  { id: 2, name: 'Usuário Padrão', ativo: true, permissions: {}, createdAt: '', updatedAt: '' },
  { id: 3, name: 'Recrutador', ativo: true, permissions: {}, createdAt: '', updatedAt: '' },
];

// --- Helper TabPanel --- (Similar ao ContractFormPage)
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`person-tabpanel-${index}`}
      aria-labelledby={`person-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 2, pb: 1 }}> 
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `person-tab-${index}`,
    'aria-controls': `person-tabpanel-${index}`,
  };
}

const PersonFormPage = () => {
  const { id } = useParams(); 
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  const [currentTab, setCurrentTab] = useState(0); // Estado para aba ativa

  const [person, setPerson] = useState<PersonFormData>({
    name: '',
    email: '',
    email_morpheus: '',
    service_provider: false,
    active: true,
    cpf: '',
    rg: '',
    birth_date: null,
    mother_name: '',
    father_name: '',
    mobile_phone: '',
    address: '',
    password: '',
    confirm_password: '',
    profile_id: null,
    hr: false
  });
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(isEditMode); 
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string}>({});
  // Estados para confirmação e snackbar
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const personService = new PersonService();
  const profileService = new ProfileService();

  useEffect(() => {
    fetchProfiles();

    if (isEditMode) {
      fetchPersonData();
    } else {
      setLoading(false); 
    }
  }, [id]);

  const fetchProfiles = async () => {
    try {
      const profileResponse = await profileService.getAll(/* filters */);
      setProfiles(profileResponse.data);
    } catch (err) {
      console.error("Erro ao buscar perfis", err);
      setError('Falha ao carregar perfis ativos.');
    }
  };

  const fetchPersonData = async () => {
    if (!id) return;
    setLoading(true);
    
    try {
      const data = await personService.getById(parseInt(id));
      
      setPerson({
        name: data.name,
        email: data.email,
        email_morpheus: data.email_morpheus || '',
        service_provider: data.service_provider,
        active: data.active,
        cpf: data.cpf,
        rg: data.rg || '',
        birth_date: data.birth_date ? data.birth_date.split('T')[0] : null,
        mother_name: data.mother_name || '',
        father_name: data.father_name || '',
        mobile_phone: data.mobile_phone || '',
        address: data.address || '',
        password: '',
        confirm_password: '',
        profile_id: data.profile_id ?? null,
        hr: data.hr
      });
      
      setError(null);
    } catch (err) {
      setError('Falha ao carregar dados da pessoa.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    let processedValue = value;
    // Trata especificamente o campo 'name' para forçar maiúsculas
    if (name === 'name') {
      processedValue = value.toUpperCase();
    }

    setPerson(prev => ({ ...prev, [name]: processedValue }));
    
    // Limpar erro específico quando o campo é editado
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({...prev, [name]: ''}));
    }
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setPerson(prev => ({ ...prev, [name]: checked }));
  };

  const handleSelectChange = (e: any) => { 
    const { name, value } = e.target;
    setPerson(prev => ({ ...prev, [name]: value === '' ? null : Number(value) }));
  };

  const validateForm = () => {
    const errors: {[key: string]: string} = {};
    
    // Nome e email são obrigatórios sempre
    if (!person.name.trim()) errors.name = 'Nome é obrigatório';
    if (!person.email.trim()) errors.email = 'Email é obrigatório';
    
    // Validar email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (person.email && !emailRegex.test(person.email)) {
      errors.email = 'Formato de email inválido';
    }
    
    // Validar email_morpheus se preenchido
    if (person.email_morpheus && !emailRegex.test(person.email_morpheus)) {
      errors.email_morpheus = 'Formato de email inválido';
    }
    
    // Validações para prestadores de serviço
    if (person.service_provider) {
      // Validar CPF
      if (!person.cpf) {
        errors.cpf = 'CPF é obrigatório';
      } else {
        const cpfClean = person.cpf.replace(/\D/g, '');
        if (cpfClean.length !== 11) {
          errors.cpf = 'CPF deve ter 11 dígitos';
        }
      }
      
      // Remover a obrigatoriedade do perfil para prestador
      // if (person.profile_id == null) errors.profile_id = 'Perfil é obrigatório'; 
    }
    
    // Validação de senha quando um perfil é selecionado (para acesso ao sistema)
    if (person.profile_id != null) {
      if (!isEditMode || (person.password || person.confirm_password)) {
        if (!person.password) errors.password = 'Senha é obrigatória quando um perfil é selecionado';
        if (person.password !== person.confirm_password) {
          errors.confirm_password = 'As senhas não coincidem';
        }
      }
    } else {
      // Se não for prestador e não tiver perfil, pode ter outras validações aqui
      // Ex: Verificar se é RH
      if(!person.hr) {
        // Talvez lançar erro ou apenas permitir salvar sem acesso
        console.warn("Pessoa sendo salva sem perfil e sem ser RH.")
      }
      
      // Para não prestadores sem perfil, validações mínimas se houver dados preenchidos
      if (person.cpf) {
        const cpfClean = person.cpf.replace(/\D/g, '');
        if (cpfClean.length !== 11) {
          errors.cpf = 'CPF deve ter 11 dígitos';
        }
      }
      
      if (person.password || person.confirm_password) {
        if (person.password !== person.confirm_password) {
          errors.confirm_password = 'As senhas não coincidem';
        }
      }
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Função que REALMENTE salva
  const handleConfirmSave = async () => {
    setIsConfirmDialogOpen(false); // Fecha diálogo

    if (!validateForm()) return;

    setSaving(true);
    setError(null);

    try {
      const personData = { ...person };
      if (personData.confirm_password === '') delete personData.confirm_password;

      let successMessage = '';
      if (isEditMode) {
        await personService.update(parseInt(id!), personData as any);
        successMessage = 'Pessoa atualizada com sucesso.';
      } else {
        await personService.create(personData as any);
        successMessage = 'Pessoa criada com sucesso.';
      }

      // Mostrar Snackbar e agendar navegação
      setSnackbarMessage(successMessage);
      setSnackbarOpen(true);
      setTimeout(() => {
        navigate('/pessoas');
      }, 1500);

    } catch (err: any) {
      if (err.response && err.response.status === 400 && err.response.data.message) {
        setError(err.response.data.message);
        if (err.response.data.message.includes('email')) {
          setFieldErrors(prev => ({...prev, email: 'Este email já está em uso'}));
        }
        if (err.response.data.message.includes('cpf')) {
          setFieldErrors(prev => ({...prev, cpf: 'Este CPF já está cadastrado'}));
        }
      } else {
        setError('Falha ao salvar pessoa. ' + (err.response?.data?.message || err.message));
      }
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // Função chamada pelo botão SALVAR (agora só abre o diálogo)
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Apenas valida e abre o diálogo se válido
    if (validateForm()) {
        setIsConfirmDialogOpen(true);
    }
  };

  // Função para fechar diálogo sem salvar
  const handleCancelSave = () => {
    setIsConfirmDialogOpen(false);
  };

  // Função para fechar o Snackbar
  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleCancel = () => {
    navigate('/pessoas'); 
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        {isEditMode ? 'Editar Pessoa' : 'Nova Pessoa'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2, py: 0.5, fontSize: '0.875rem' }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Card elevation={2}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                fontSize: '0.85rem',
                py: 1,
                minHeight: 40
              }
            }}
          >
            <Tab label="Dados Pessoais" {...a11yProps(0)} />
            <Tab label="Acesso" {...a11yProps(1)} />
          </Tabs>

          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <TabPanel value={currentTab} index={0}>
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nome Completo"
                    name="name"
                    value={person.name}
                    onChange={handleChange}
                    required
                    margin="dense"
                    size="small"
                    error={!!fieldErrors.name}
                    helperText={fieldErrors.name}
                    InputProps={{ 
                      style: { fontSize: '0.85rem' },
                      sx: { height: 40 }
                    }}
                    InputLabelProps={{ 
                      style: { fontSize: '0.85rem' } 
                    }}
                    FormHelperTextProps={{
                      style: { fontSize: '0.75rem', marginTop: '2px' }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="email"
                    label="Email Pessoal"
                    type="email"
                    value={person.email || ''}
                    onChange={handleChange}
                    fullWidth
                    required
                    disabled={saving}
                    margin="dense"
                    size="small"
                    error={!!fieldErrors.email}
                    helperText={fieldErrors.email}
                    InputProps={{
                      style: { fontSize: '0.85rem' },
                      sx: { height: 40 }
                    }}
                    InputLabelProps={{
                      style: { fontSize: '0.85rem' }
                    }}
                    FormHelperTextProps={{
                      style: { fontSize: '0.75rem', marginTop: '2px' }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="email_morpheus"
                    label="Email Morpheus"
                    type="email"
                    value={person.email_morpheus || ''}
                    onChange={handleChange}
                    fullWidth
                    disabled={saving}
                    margin="dense"
                    size="small"
                    error={!!fieldErrors.email_morpheus}
                    helperText={fieldErrors.email_morpheus}
                    InputProps={{
                      style: { fontSize: '0.85rem' },
                      sx: { height: 40 }
                    }}
                    InputLabelProps={{
                      style: { fontSize: '0.85rem' }
                    }}
                    FormHelperTextProps={{
                      style: { fontSize: '0.75rem', marginTop: '2px' }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sx={{mt: 0, mb: 1}}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="service_provider"
                        checked={person.service_provider}
                        onChange={handleSwitchChange}
                        size="small"
                      />
                    }
                    label={<Typography sx={{ fontSize: '0.85rem' }}>Prestador de Serviço</Typography>}
                    sx={{ mb: 1 }}
                  />
                </Grid>
              </Grid>

              {person.service_provider && (
                <Box sx={{ mt: 1, mb: 1, p: 1.5, border: '1px dashed grey', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Dados Complementares</Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6} md={4}>
                      <InputMask
                        mask="999.999.999-99"
                        value={person.cpf || ''}
                        onChange={handleChange}
                        disabled={saving}
                      >
                        {(inputProps: any) => (
                          <TextField
                            {...inputProps}
                            name="cpf"
                            label="CPF"
                            fullWidth
                            required
                            margin="dense"
                            size="small"
                            error={!!fieldErrors.cpf}
                            helperText={fieldErrors.cpf}
                            InputProps={{
                              style: { fontSize: '0.85rem' },
                              sx: { height: 40 }
                            }}
                            InputLabelProps={{
                              style: { fontSize: '0.85rem' }
                            }}
                          />
                        )}
                      </InputMask>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField 
                        name="rg" 
                        label="RG" 
                        value={person.rg || ''} 
                        onChange={handleChange} 
                        fullWidth 
                        disabled={saving} 
                        margin="dense" 
                        size="small" 
                        InputProps={{ 
                          style: { fontSize: '0.85rem' }, 
                          sx: { height: 40 } 
                        }} 
                        InputLabelProps={{ 
                          style: { fontSize: '0.85rem' } 
                        }} 
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField 
                        name="birth_date" 
                        label="Data Nascimento" 
                        type="date" 
                        value={person.birth_date ?? ''}
                        onChange={handleChange} 
                        fullWidth 
                        disabled={saving} 
                        margin="dense" 
                        size="small" 
                        InputProps={{ 
                          style: { fontSize: '0.85rem' }, 
                          sx: { height: 40 } 
                        }} 
                        InputLabelProps={{ 
                          style: { fontSize: '0.85rem' },
                          shrink: true
                        }} 
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <InputMask
                        mask="(99) 99999-9999"
                        value={person.mobile_phone || ''}
                        onChange={handleChange}
                        disabled={saving}
                      >
                        {(inputProps: any) => (
                          <TextField
                            {...inputProps}
                            name="mobile_phone"
                            label="Telefone Celular"
                            fullWidth
                            margin="dense"
                            size="small"
                            InputProps={{
                              style: { fontSize: '0.85rem' },
                              sx: { height: 40 }
                            }}
                            InputLabelProps={{
                              style: { fontSize: '0.85rem' }
                            }}
                          />
                        )}
                      </InputMask>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField 
                        name="mother_name" 
                        label="Nome da Mãe" 
                        value={person.mother_name || ''} 
                        onChange={handleChange} 
                        fullWidth 
                        disabled={saving} 
                        margin="dense" 
                        size="small" 
                        InputProps={{ 
                          style: { fontSize: '0.85rem' } 
                        }} 
                        InputLabelProps={{ 
                          style: { fontSize: '0.85rem' } 
                        }} 
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField 
                        name="father_name" 
                        label="Nome do Pai" 
                        value={person.father_name || ''} 
                        onChange={handleChange} 
                        fullWidth 
                        disabled={saving} 
                        margin="dense" 
                        size="small" 
                        InputProps={{ 
                          style: { fontSize: '0.85rem' } 
                        }} 
                        InputLabelProps={{ 
                          style: { fontSize: '0.85rem' } 
                        }} 
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField 
                        name="address" 
                        label="Endereço" 
                        value={person.address || ''} 
                        onChange={handleChange} 
                        fullWidth 
                        disabled={saving} 
                        margin="dense" 
                        multiline 
                        rows={1} 
                        size="small" 
                        InputProps={{ 
                          style: { fontSize: '0.85rem' } 
                        }} 
                        InputLabelProps={{ 
                          style: { fontSize: '0.85rem' } 
                        }} 
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
              <Grid container sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Switch checked={person.active} onChange={handleSwitchChange} name="active" disabled={saving} size="small" />}
                    label={<Typography sx={{ fontSize: '0.85rem' }}>Ativo</Typography>}
                    sx={{ mb: 1 }}
                  />
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value={currentTab} index={1}>
              <Box sx={{ mb: 2, p: 1.5, bgcolor: 'info.lighter', borderRadius: 1 }}>
                <Typography sx={{ fontSize: '0.85rem' }}>
                  Para liberar o acesso ao Siga RH para este usuário, basta selecionar um perfil e definir uma senha.
                  A senha é obrigatória quando um perfil é selecionado.
                </Typography>
              </Box>
              
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <FormControl 
                    fullWidth 
                    margin="dense"
                    size="small"
                    error={!!fieldErrors.profile_id}
                  >
                    <InputLabel id="profile-select-label" sx={{ fontSize: '0.85rem' }}>Perfil</InputLabel>
                    <Select
                      labelId="profile-select-label"
                      name="profile_id"
                      value={person.profile_id ?? ''}
                      onChange={handleSelectChange}
                      displayEmpty
                      sx={{ fontSize: '0.85rem', height: 40 }}
                      endAdornment={
                        person.profile_id != null ? (
                          <InputAdornment position="end" sx={{ mr: 2.5 }}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setPerson(prev => ({ ...prev, profile_id: null }));
                              }}
                              edge="end"
                              aria-label="Limpar seleção de perfil"
                            >
                              <X size={14} />
                            </IconButton>
                          </InputAdornment>
                        ) : null
                      }
                    >
                      {profiles.map(profile => (
                        <MenuItem key={profile.id} value={profile.id} sx={{ fontSize: '0.85rem' }}>
                          {profile.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {fieldErrors.profile_id && (
                      <FormHelperText sx={{ fontSize: '0.75rem' }}>{fieldErrors.profile_id}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}></Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    name="password"
                    label="Senha"
                    type="password"
                    value={person.password || ''}
                    onChange={handleChange}
                    fullWidth
                    disabled={saving}
                    margin="dense"
                    size="small"
                    error={!!fieldErrors.password}
                    helperText={fieldErrors.password}
                    InputProps={{ 
                      style: { fontSize: '0.85rem' },
                      sx: { height: 40 }
                    }}
                    InputLabelProps={{ 
                      style: { fontSize: '0.85rem' } 
                    }}
                    FormHelperTextProps={{
                      style: { fontSize: '0.75rem', marginTop: '2px' }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="confirm_password"
                    label="Confirmar Senha"
                    type="password"
                    value={person.confirm_password || ''}
                    onChange={handleChange}
                    fullWidth
                    disabled={saving}
                    margin="dense"
                    size="small"
                    error={!!fieldErrors.confirm_password}
                    helperText={fieldErrors.confirm_password}
                    InputProps={{ 
                      style: { fontSize: '0.85rem' },
                      sx: { height: 40 }
                    }}
                    InputLabelProps={{ 
                      style: { fontSize: '0.85rem' } 
                    }}
                    FormHelperTextProps={{
                      style: { fontSize: '0.75rem', marginTop: '2px' }
                    }}
                  />
                </Grid>
              </Grid>
            </TabPanel>
          </CardContent>

          <CardContent sx={{ pt: 1, pb: 2, px: 2 }}>
            <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                startIcon={<ArrowLeft size={16} />}
                disabled={saving}
                size="small"
                sx={{ 
                  px: 2, 
                  py: 0.5,
                  height: 36,
                  fontSize: '0.75rem',
                  borderRadius: 1
                }}
              >
                CANCELAR
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save size={16} />}
                disabled={saving}
                size="small"
                sx={{ 
                  px: 2, 
                  py: 0.5,
                  height: 36,
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  borderRadius: 1
                }}
              >
                {saving ? 'SALVANDO...' : 'SALVAR'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </form>

      {/* Componentes de Confirmação e Sucesso */}
      <ConfirmationDialog
        open={isConfirmDialogOpen}
        onClose={handleCancelSave}
        onConfirm={handleConfirmSave}
        title={isEditMode ? "Confirmar Alteração" : "Confirmar Criação"}
        contentText={`Tem certeza que deseja salvar ${isEditMode ? 'as alterações nesta' : 'esta nova'} pessoa?`}
        isLoading={saving}
        confirmText={saving ? 'Salvando...' : 'Confirmar'}
      />

      <SuccessSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        onClose={handleSnackbarClose}
      />

    </Box>
  );
};

export default PersonFormPage; 